import { useState } from 'react';
import { Box, Checkbox, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, Icon, stitchOutlineBtn, stitchSectionLabel } from '../parts/stitch';
import { formatDate } from '../parts/projectMeta';
import { useProjectData } from '../ProjectDataContext';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import OneNoteVoorstellen from './OneNoteVoorstellen';

/**
 * Het koppelscherm: meetings (en OneNote) bij dit project — twee lagen:
 * - Gekoppeld: door een mens aangevinkt; telt mee en voedt de dossier-afleiding.
 * - Voorstellen (wiki 26 sep): op projectnummer, klantnaam, deelnemers
 *   (contactpersoon) en het e-maildomein van de klant, met de reden erbij.
 *   Vinkje = koppelen; vinkje uit = ontkoppelen. Beide met "Ongedaan maken".
 */
function MeetingsPanel() {
  const navigate = useNavigate();
  const {
    id, transcripts, meetingSuggesties, loadMeetings, projectNumber, project,
  } = useProjectData();
  const [busyId, setBusyId] = useState(null);

  const koppelApi = async (meetingId) => {
    const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/meetings`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ meetingId, projectNumber }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  };
  const ontkoppelApi = async (meetingId) => {
    const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/meetings/${meetingId}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
  };

  const wissel = async (meeting, gekoppeld) => {
    if (busyId) return;
    setBusyId(meeting.id);
    const titel = String(meeting.title || 'Meeting').slice(0, 50);
    try {
      if (gekoppeld) {
        await ontkoppelApi(meeting.id);
        await loadMeetings();
        biedOngedaanAan(`"${titel}" ontkoppeld`, async () => { await koppelApi(meeting.id); await loadMeetings(); });
      } else {
        await koppelApi(meeting.id);
        await loadMeetings();
        biedOngedaanAan(`"${titel}" gekoppeld`, async () => { await ontkoppelApi(meeting.id); await loadMeetings(); });
      }
    } catch (e) { toast.error(`${gekoppeld ? 'Ontkoppelen' : 'Koppelen'} mislukt: ${e.message}`); }
    finally { setBusyId(null); }
  };

  const rij = (meeting, gekoppeld) => {
    const sub = [
      meeting.meeting_date ? formatDate(meeting.meeting_date) : '',
      gekoppeld ? '' : (meeting.redenen || []).join(' · '),
    ].filter(Boolean).join(' · ');
    return (
      <Box key={meeting.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: `1px solid ${T.surfaceLow}` }}>
        <Checkbox size="small" checked={gekoppeld} disabled={busyId === meeting.id}
          onChange={() => wissel(meeting, gekoppeld)} sx={{ p: 0.5 }}
          inputProps={{ 'aria-label': `${meeting.title || 'Meeting'} ${gekoppeld ? 'ontkoppelen' : 'koppelen'}` }} />
        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate(`/MeetingDashboard/${meeting.id}`)}>
          <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text, '&:hover': { textDecoration: 'underline' } }}>
            {meeting.title || 'Meeting'}
          </Typography>
          {sub && <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>{sub}</Typography>}
        </Box>
        {busyId === meeting.id && <CircularProgress size={14} />}
      </Box>
    );
  };

  const klantRaw = project?.fields?.KlantnaamString;
  const klantNaam = Array.isArray(klantRaw) ? (klantRaw[0] || '') : (klantRaw || '');

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography sx={{ ...stitchSectionLabel }}>
          Gekoppeld aan dit project ({transcripts.length})
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box component="span"
          onClick={() => navigate(`/MeetingDashboard${klantNaam ? `?klant=${encodeURIComponent(klantNaam)}` : ''}`)}
          sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}>
          <Icon name="mic" size={15} /> Naar Meetings
        </Box>
      </Box>
      {transcripts.length === 0 ? (
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 2 }}>
          Nog geen meetings gekoppeld — vink hieronder aan welke opnames bij dit project horen.
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>{transcripts.map((m) => rij(m, true))}</Box>
      )}

      <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>
        Voorstellen ({meetingSuggesties.length})
      </Typography>
      {meetingSuggesties.length === 0 ? (
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
          Geen meetings gevonden op projectnummer, klantnaam, contactpersoon of e-maildomein van de klant.
        </Typography>
      ) : (
        <Box>{meetingSuggesties.map((m) => rij(m, false))}</Box>
      )}

      <OneNoteVoorstellen />
    </>
  );
}

export default MeetingsPanel;
